import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { observer } from "mobx-react-lite";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ExtraVACallsPlan } from "../../../../../services/organization-subscription-plans/SubscriptionPlan";
import { translationService } from "../../../../../services/translation/TranslationService";
import { NumberSelection } from "../../../../../components/number-selection/NumberSelection";
import { InfoTooltip } from "../info-tooltip/InfoTooltip";

import "./composition.scss";

interface ExtraVideoAnalysisProps {
  extraVACalls: ExtraVACallsPlan;
  baseExtraVACallsPlan: ExtraVACallsPlan;
  totalExtraVACallsAmountChanged: (newAmount: number) => void;
  nextBillingDate?: Date;
  isActiveInNextBillingPeriod: boolean;
  currentPlanWillEndAndIsNotCanceled: boolean;
  currentSubscriptionWillBeRenewed: boolean;
  getLocalizedPrice: (price: number) => string;
}

export const ExtraVideoAnalysis = observer((props: ExtraVideoAnalysisProps) => {
  const MAX_AMOUNT = 9999;
  const {
    extraVACalls,
    baseExtraVACallsPlan,
    totalExtraVACallsAmountChanged,
    nextBillingDate,
    isActiveInNextBillingPeriod,
    currentPlanWillEndAndIsNotCanceled,
    currentSubscriptionWillBeRenewed,
    getLocalizedPrice,
  } = props;

  const [callsAmount, setCallsAmount] = useState(
    extraVACalls.activeSubscriptionPackageAmount ||
      baseExtraVACallsPlan.previouslySelectedAmount ||
      0
  );

  const [totalCallsPrice, setTotalCallsPrice] = useState(
    extraVACalls.activeSubscriptionPackageAmount
      ? extraVACalls.activeSubscriptionPackageAmount * baseExtraVACallsPlan.priceAmount
      : baseExtraVACallsPlan.previouslySelectedAmount
      ? baseExtraVACallsPlan.previouslySelectedAmount * baseExtraVACallsPlan.priceAmount
      : 0
  );

  const initialValue = useMemo(() => {
    return (
      extraVACalls.activeSubscriptionPackageAmount ||
      baseExtraVACallsPlan.previouslySelectedAmount ||
      0
    );
  }, [extraVACalls.activeSubscriptionPackageAmount, baseExtraVACallsPlan.previouslySelectedAmount]);

  const extraVACallsAmountChanged = useCallback(
    (newAmount: number) => {
      setCallsAmount(newAmount);
      setTotalCallsPrice(newAmount * baseExtraVACallsPlan.priceAmount);
    },
    [baseExtraVACallsPlan.priceAmount]
  );

  useEffect(() => {
    totalExtraVACallsAmountChanged(callsAmount);
  }, [totalExtraVACallsAmountChanged, callsAmount]);

  useEffect(() => {
    if (baseExtraVACallsPlan.previouslySelectedAmount) {
      setCallsAmount(baseExtraVACallsPlan.previouslySelectedAmount);
      setTotalCallsPrice(
        baseExtraVACallsPlan.previouslySelectedAmount * baseExtraVACallsPlan.priceAmount
      );
    }
  }, [baseExtraVACallsPlan.previouslySelectedAmount, baseExtraVACallsPlan.priceAmount]);

  return (
    <>
      <Accordion id={extraVACalls.name} className="accordion">
        <AccordionSummary className="accordion-header" expandIcon={<ExpandMoreIcon />}>
          <div className="medium-label">
            {`${translationService.translate("SubscriptionPlans.card.extra-video-analysis")} -
            ${
              extraVACalls.activeSubscriptionPackageAmount ||
              baseExtraVACallsPlan.previouslySelectedAmount ||
              0
            }
            ${translationService.translate(
              "SubscriptionPlans.card.extra-video-analysis-packages"
            )}`}
          </div>
        </AccordionSummary>
        <AccordionDetails className="accordion-content">
          <div className="number-selection-container">
            <NumberSelection
              initialValue={initialValue}
              minValue={currentPlanWillEndAndIsNotCanceled ? initialValue : 0}
              maxValue={MAX_AMOUNT}
              labelTranslationKey={translationService.translate(
                "SubscriptionPlans.card.number-of-calls",
                {
                  numberOfCalls: baseExtraVACallsPlan.videoAnalyserCalls,
                }
              )}
              callback={extraVACallsAmountChanged}
              minusButtonDisabled={
                callsAmount <= 0 ||
                (currentPlanWillEndAndIsNotCanceled && callsAmount <= initialValue)
              }
              plusButtonDisabled={callsAmount >= MAX_AMOUNT}
            />
            {isActiveInNextBillingPeriod && currentSubscriptionWillBeRenewed && nextBillingDate && (
              <InfoTooltip
                nextBillingDate={nextBillingDate}
                firstBoldedTextKey="SubscriptionPlans.card.info.adding"
                firstTextKey="SubscriptionPlans.card.video-analysis.info.adding"
                secondBoldedTextKey="SubscriptionPlans.card.info.removing"
                secondTextKey="SubscriptionPlans.card.video-analysis.info.removing"
              />
            )}
            {isActiveInNextBillingPeriod &&
              !currentSubscriptionWillBeRenewed &&
              nextBillingDate && (
                <InfoTooltip
                  nextBillingDate={nextBillingDate}
                  firstBoldedTextKey="SubscriptionPlans.card.info.adding-or-removing"
                  firstTextKey="SubscriptionPlans.card.video-analysis.downgrade.info"
                />
              )}
            {currentPlanWillEndAndIsNotCanceled && nextBillingDate && (
              <InfoTooltip
                nextBillingDate={nextBillingDate}
                firstBoldedTextKey="SubscriptionPlans.card.info.removing"
                firstTextKey="SubscriptionPlans.card.video-analysis.info.removing-ending-plan"
              />
            )}
          </div>
          <div className="small-opacity-label">
            {`(${getLocalizedPrice(baseExtraVACallsPlan.priceAmount)} € /
            ${baseExtraVACallsPlan.videoAnalyserCalls} ${translationService.translate(
              "SubscriptionPlans.card.calls"
            )} /
            ${translationService.translate("SubscriptionPlans.card.three-months")})`}
          </div>
          <div>
            <span className="large-label">{`${getLocalizedPrice(totalCallsPrice)} € / `}</span>
            {`${
              callsAmount * baseExtraVACallsPlan.videoAnalyserCalls
            } ${translationService.translate("SubscriptionPlans.card.calls")} /
            ${translationService.translate("SubscriptionPlans.card.three-months")}`}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
});
